import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config/input.type.constants'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { openS3Document } from 'services'
import { getContractorConsentPDF, updateContractorApplication } from 'services/apis'
import { Button, Checkbox, FormTableHeader, PlainTable } from 'stories/components'
import { formatTime, replaceNextLineToBr } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { ContractorSignDialog } from '../ContractorSignDialog'

type ContractorConsentFormProps = {
  consentContent: string
  agreeConsent: boolean
  onAgreeConsentUpdated: (value: boolean) => void
  canEdit: boolean
  showHistory: (key: string, headers?: Array<FormTableHeader>) => void
  consentSignData: { fileKey?: string; override?: boolean; time?: string }
  onConsentSignDataChange: (data: any) => void
  contractorGeneralInfo: Record<string, InputType>
  contractorURL: any
  canOverride: boolean
  setLoading: (v: boolean) => void
  onSigned?: () => void
}

export function ContractorConsentForm({
  consentContent,
  agreeConsent,
  onAgreeConsentUpdated,
  canEdit,
  showHistory,
  consentSignData,
  onConsentSignDataChange,
  contractorGeneralInfo,
  contractorURL,
  canOverride,
  setLoading,
  onSigned,
}: ContractorConsentFormProps) {
  const { no: noParam } = useParams() as { no: string }
  const no = Number(noParam)
  const [showSignModal, setShowSignModal] = useState(false)

  const onChangeAgreeConsent = async (key: string, value: boolean) => {
    setLoading(true)
    await updateContractorApplication(no, { [key]: value })
    onAgreeConsentUpdated(value)
    setLoading(false)
  }

  const onOverride = async () => {
    setLoading(true)
    const temp = cloneDeep(consentSignData)
    temp.override = !consentSignData.override
    temp.time = formatTime()
    onConsentSignDataChange(temp)
    await updateContractorApplication(no, { consentSignData: temp })
    setLoading(false)
  }

  const consentPDF = async () => {
    setLoading(true)
    if (consentSignData.fileKey) openS3Document(consentSignData.fileKey, true)
    else {
      const res = await getContractorConsentPDF(no)
      if (res.success) {
        const a = document.createElement('a')
        a.href = res.content
        a.download = 'Contractor_Consent_Form.pdf'
        a.click()
      }
    }
    setLoading(false)
    return true
  }

  const onAfterSignFromDialog = (data: any) => {
    onConsentSignDataChange(data)
    setTimeout(() => {
      onSigned?.()
    }, 300)
  }

  const isSigned = consentSignData.fileKey
  const isOverridden = consentSignData.override
  const validation =
    !agreeConsent && !consentSignData.override ? (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-[14px]"
        role="alert"
      >
        You must agree to the consent form before signing.
      </div>
    ) : null

  const tableData = [
    [
      contractorGeneralInfo.entityEmail?.value || '',
      <p className="font-['freehand'] text-[24px]" key="sig-p">
        {contractorGeneralInfo.firstName?.value || ''} {contractorGeneralInfo.lastName?.value || ''}
      </p>,
      isOverridden ? 'Overridden' : isSigned ? 'Signed' : 'Unsigned',
      isSigned || isOverridden ? formatTime(consentSignData.time) : '',
      <div
        key="pdf"
        className="flex items-center gap-2 cursor-pointer hover:underline text-shade-blue font-variation-settings-600"
        onClick={consentPDF}
      >
        {isSigned
          ? [<span key={0}>View Signed Consent</span>, <EyeIcon key={1} className="w-4 h-4"></EyeIcon>]
          : [
              <span key={0}>View Unsigned Consent</span>,
              <ArrowDownTrayIcon key={1} className="w-4 h-4"></ArrowDownTrayIcon>,
            ]}
      </div>,
      <Checkbox
        key="override"
        id="consentOverride"
        title=""
        value={!!consentSignData.override}
        onChange={onOverride}
        disabled={!canOverride}
        history={true}
        showHistory={() => showHistory('consentSignData')}
      />,
    ],
  ]

  return (
    <div className="md:px-2">
      <div
        className="mb-5"
        dangerouslySetInnerHTML={{ __html: replaceNextLineToBr(consentContent) }}
      />
      <div className="mb-4">
        <RenderInput
          input={{
            inputType: 'toggle',
            title: 'I have read and agree to the Contractor Consent Form.',
            error: agreeConsent ? '' : 'Required!',
            value: agreeConsent,
            history: true,
            disabled: !canEdit || !!isSigned,
          }}
          Key={'agreeConsent'}
          onChange={onChangeAgreeConsent}
          showHistory={showHistory}
        />
      </div>
      <div>
        <div className="font-semibold mb-3">- Sign Consent Form</div>
        {validation}
        <div className="overflow-x-auto">
          <div className="w-fit">
            <PlainTable
              header={['Email', 'Signature', 'Status', 'Signed Date', 'PDF', 'Override']}
              data={tableData}
            />
          </div>
        </div>
        {contractorURL && !isSigned && !isOverridden && (
          <div className="flex flex-wrap gap-4 mt-4">
            <Button onClick={() => setShowSignModal(true)} disabled={validation !== null}>
              Click Here to Sign Consent Form
            </Button>
          </div>
        )}
      </div>
      {showSignModal && (
        <ContractorSignDialog
          signType="consent"
          onSign={onAfterSignFromDialog}
          onClose={() => setShowSignModal(false)}
          applicationPDF={consentPDF}
          title="Confirm and Sign Consent Form"
          titleOkay="Confirm and Sign"
          reviewedLabel="I have reviewed the consent form"
          successHint="Upon successful signing, you may proceed to Acknowledgement & Sign."
        />
      )}
    </div>
  )
}
